import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import Layout from '../components/Layout'
import CopyButton from '../components/CopyButton'
import { addToHistory } from '../utils/history'

type Mode = 'encode' | 'decode'
type EncodeType = 'component' | 'full'

function UrlEncode() {
  const { t } = useTranslation()
  const [mode, setMode] = useState<Mode>('encode')
  const [encodeType, setEncodeType] = useState<EncodeType>('component')
  const [input, setInput] = useState('')
  const [output, setOutput] = useState('')
  const [error, setError] = useState('')

  const saveToHistory = useCallback((inputVal: string, outputVal: string) => {
    if (inputVal && outputVal) {
      addToHistory({
        tool: 'url-encode',
        toolName: 'URL Encode',
        input: inputVal.substring(0, 500),
        output: outputVal.substring(0, 500),
      })
    }
  }, [])

  const encode = (text: string) => {
    setInput(text)
    setError('')
    
    if (!text) {
      setOutput('')
      return
    }

    try {
      const result = encodeType === 'component' ? encodeURIComponent(text) : encodeURI(text)
      setOutput(result)
      saveToHistory(text, result)
    } catch (err) {
      setError(t('toolPages.urlEncode.encodeError'))
      setOutput('')
    }
  }

  const decode = (text: string) => {
    setInput(text)
    setError('')
    
    if (!text) {
      setOutput('')
      return
    }

    try {
      const result = encodeType === 'component' ? decodeURIComponent(text) : decodeURI(text)
      setOutput(result)
      saveToHistory(text, result)
    } catch (err) {
      setError(t('toolPages.urlEncode.decodeError'))
      setOutput('')
    }
  }

  const handleInputChange = (value: string) => {
    if (mode === 'encode') {
      encode(value)
    } else {
      decode(value)
    }
  }

  const handleModeChange = (newMode: Mode) => {
    setMode(newMode)
    setInput('')
    setOutput('')
    setError('')
  }

  const handleEncodeTypeChange = (newType: EncodeType) => {
    setEncodeType(newType)
    if (input) {
      if (mode === 'encode') {
        try {
          if (newType === 'component') {
            setOutput(encodeURIComponent(input))
          } else {
            setOutput(encodeURI(input))
          }
        } catch {
          setError(t('toolPages.urlEncode.encodeError'))
        }
      } else {
        try {
          if (newType === 'component') {
            setOutput(decodeURIComponent(input))
          } else {
            setOutput(decodeURI(input))
          }
        } catch {
          setError(t('toolPages.urlEncode.decodeError'))
        }
      }
    }
  }

  const handleSwap = () => {
    if (output) {
      const newInput = output
      if (mode === 'encode') {
        setMode('decode')
        decode(newInput)
      } else {
        setMode('encode')
        encode(newInput)
      }
    }
  }

  const handleClear = () => {
    setInput('')
    setOutput('')
    setError('')
  }

  const loadSample = () => {
    const sample = mode === 'encode' 
      ? 'https://example.com/search?q=안녕하세요&category=개발자 도구&page=1'
      : 'https%3A%2F%2Fexample.com%2Fsearch%3Fq%3D%EC%95%88%EB%85%95%ED%95%98%EC%84%B8%EC%9A%94%26category%3D%EA%B0%9C%EB%B0%9C%EC%9E%90%20%EB%8F%84%EA%B5%AC%26page%3D1'
    handleInputChange(sample)
  }

  return (
    <Layout showBackButton title={t('toolPages.urlEncode.title')}>
      <div className="tool-container">
        <p className="tool-description">
          {t('toolPages.urlEncode.description')}
        </p>

      {/* Controls */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="flex rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
          <button
            onClick={() => handleModeChange('encode')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              mode === 'encode'
                ? 'bg-primary-600 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            {t('common.encode')}
          </button>
          <button
            onClick={() => handleModeChange('decode')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              mode === 'decode'
                ? 'bg-primary-600 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            {t('common.decode')}
          </button>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('toolPages.urlEncode.method')}:</span>
          <div className="flex rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
            <button
              onClick={() => handleEncodeTypeChange('component')}
              className={`px-3 py-2 text-sm font-medium transition-colors ${
                encodeType === 'component'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              Component
            </button>
            <button
              onClick={() => handleEncodeTypeChange('full')}
              className={`px-3 py-2 text-sm font-medium transition-colors ${
                encodeType === 'full'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              Full URI
            </button>
          </div>
        </div>

        <div className="flex gap-2">
          <button onClick={loadSample} className="btn-outline">
            {t('common.sample')}
          </button>
          <button onClick={handleClear} className="btn-secondary">
            {t('common.clear')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="card">
          <div className="flex justify-between items-center mb-3">
            <h2 className="section-title">
              {t('common.input')}
            </h2>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {input.length > 0 ? `${input.length}` : ''}
            </span>
          </div>
          <textarea
            value={input}
            onChange={(e) => handleInputChange(e.target.value)}
            placeholder={t('common.input') + '...'}
            className="textarea h-48 scrollbar-thin"
            spellCheck={false}
          />
          {error && (
            <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="error-message flex items-start gap-2">
                <span>⚠️</span>
                <span>{error}</span>
              </p>
            </div>
          )}
        </div>

        {/* Output Section */}
        <div className="card">
          <div className="flex justify-between items-center mb-3">
            <h2 className="section-title">
              {t('common.output')}
            </h2>
            <div className="flex gap-2">
              <button onClick={handleSwap} className="btn-secondary text-sm" disabled={!output}>
                ⇄ {t('common.swap')}
              </button>
              <CopyButton text={output} />
            </div>
          </div>
          <textarea
            value={output}
            readOnly
            placeholder={t('common.output') + '...'}
            className="textarea h-48 bg-gray-50 dark:bg-gray-900 scrollbar-thin"
            spellCheck={false}
          />
        </div>
      </div>
      </div>
    </Layout>
  )
}

export default UrlEncode
